import { PauseIcon, PlayIcon, Undo2Icon } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useEffect, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import { CardFooter } from "@/components/ui/card";
import { cn } from "@/lib/utils";

type FooterProps = {
  currentIndex: number;
  isAutoPlay: boolean;
  undo: () => void;
  toggleAutoPlay: () => void;
};

export function Footer({ currentIndex, isAutoPlay, undo, toggleAutoPlay }: FooterProps) {
  const [showHint, setShowHint] = useState(true);
  const [autoPlayMessage, setAutoPlayMessage] = useState<string | null>(null);
  const messageTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setShowHint(false), 3000);
    return () => clearTimeout(timer);
  }, []);

  function handleToggleAutoPlay() {
    if (messageTimerRef.current) {
      clearTimeout(messageTimerRef.current);
    }
    const next = !isAutoPlay;
    setAutoPlayMessage(next ? "자동 플레이가 켜져 있습니다." : "자동 플레이가 꺼져 있습니다.");
    messageTimerRef.current = setTimeout(() => setAutoPlayMessage(null), 3000);
    toggleAutoPlay();
  }

  return (
    <CardFooter className="flex flex-col items-center gap-2 bg-transparent border-t-0 p-6 pt-0">
      <div className="flex items-center justify-between w-full">
        {/* 되돌리기 버튼 */}
        <Button variant="ghost" size="icon" onClick={undo} disabled={currentIndex === 0} className="size-12 rounded-xl">
          <Undo2Icon className="size-6 text-muted-foreground" />
        </Button>

        {/* 메시지 */}
        <AnimatePresence mode="wait">
          {autoPlayMessage ? (
            <motion.p
              key="autoplay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4 }}
              className="text-xs text-muted-foreground text-center"
            >
              {autoPlayMessage}
            </motion.p>
          ) : showHint ? (
            <motion.p
              key="hint"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4 }}
              className="text-xs text-muted-foreground text-center"
            >
              카드를 <b>탭</b>하면 <b>뜻</b>이 보이고,
              <br />
              <b>왼쪽</b>으로 넘기면 <b>학습 중</b>,
              <br />
              <b>오른쪽</b>으로 넘기면 <b>알고 있음</b>으로 처리됩니다.
            </motion.p>
          ) : null}
        </AnimatePresence>

        {/* 자동 플레이 버튼 */}
        <Button
          variant="ghost"
          size="icon"
          onClick={handleToggleAutoPlay}
          className={cn("size-12 rounded-xl", isAutoPlay && "bg-primary-background")}
        >
          {isAutoPlay ? (
            <PauseIcon className="size-6 text-muted-foreground" />
          ) : (
            <PlayIcon className="size-6 text-muted-foreground" />
          )}
        </Button>
      </div>
    </CardFooter>
  );
}
